import { useState } from "react";
import type { DtoAddress } from "@porchpass/api-client";
import DeleteAddressModal from "./DeleteAddressModal";
import { toFormattedInteger } from "src/utils";
import {
  FormTableSummaryCard,
  Location03Icon,
  LocationStar01Icon,
  LocationUpdate01Icon,
} from "@porchpass/ui";

const getAddressTitle = (address: DtoAddress) => {
  if (!address.street1?.trim()) {
    return "Address Unknown";
  }

  return address.street1;
};

const formatDateRange = (from: string, to?: string): string => {
  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      timeZone: "UTC",
    });

  return to
    ? `From ${formatDate(from)} to ${formatDate(to)}`
    : `Since ${formatDate(from)}`;
};

const getHousingText = (type?: string) => {
  const housingTypes: Record<string, string> = {
    OTHER: "Other",
    OWN: "Own",
    RENT: "Rent",
    NO_PRIMARY_HOUSING_EXPENSE: "No Primary Housing Expense",
  };

  return housingTypes[type ?? ""];
};

type AddressItemProps = {
  address: DtoAddress;
  index?: number;
  editHandler?: () => void;
  closeHandler?: () => void;
  deleteHandler?: () => void;
  isLoading?: boolean;
};

export default ({
  address,
  index = 0,
  closeHandler,
  editHandler,
  deleteHandler,
  isLoading,
}: AddressItemProps) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  if (!address) return null;

  const isCurrent = address.address_type === "CURRENT";
  const isMailing = address.address_type === "MAILING";
  const isPrevious = address.address_type === "PREVIOUS";
  const isUnknown = !address.street1?.trim();

  const cityStateZip = [address.city, address.state, address.zip_code]
    .filter(Boolean)
    .join(", ");

  const subtitles: string[] = [];
  if (cityStateZip) subtitles.push(cityStateZip);

  if (isCurrent && address.resided_from) {
    subtitles.push(formatDateRange(address.resided_from));
    if (address.housing_type) {
      subtitles.push(getHousingText(address.housing_type) ?? "");
    }
  }

  if (isPrevious && address.resided_from) {
    subtitles.push(formatDateRange(address.resided_from, address.resided_to));
  }

  const badges = [];
  if (isCurrent) badges.push({ label: "Current", variant: "primary" as const });
  if (isMailing || (isCurrent && address.is_mailing_address)) {
    badges.push({ label: "Mailing", variant: "default" as const });
  }

  const IconComponent = isCurrent
    ? LocationStar01Icon
    : isMailing
      ? Location03Icon
      : LocationUpdate01Icon;

  const readOnly = !editHandler || !deleteHandler || !closeHandler;

  return (
    <>
      <FormTableSummaryCard
        index={index}
        title={getAddressTitle(address)}
        isUnknown={isUnknown}
        icon={
          <IconComponent
            variant="bulk-rounded"
            size={16}
            className={
              isCurrent || isMailing ? "text-primary" : "text-[#4a5466]"
            }
          />
        }
        iconTone={isCurrent || isMailing ? "primary" : "default"}
        highlight={isCurrent || isMailing ? "primary" : "none"}
        badges={badges}
        subtitles={subtitles.filter(Boolean)}
        amount={
          isCurrent && address.monthly_payment != null
            ? {
                value: `$${toFormattedInteger(address.monthly_payment)}`,
                suffix:
                  Number(address.monthly_payment) === 0 ? "mo / ann" : "mo",
              }
            : undefined
        }
        onEdit={editHandler}
        onDelete={deleteHandler ? () => setIsDeleteModalOpen(true) : undefined}
        readOnly={readOnly || isLoading}
        onClick={editHandler}
      />
      {deleteHandler && closeHandler && (
        <DeleteAddressModal
          isOpen={isDeleteModalOpen}
          address_id={address.id ?? ""}
          closeHandler={() => {
            setIsDeleteModalOpen(false);
            closeHandler();
          }}
          deleteHandler={() => {
            setIsDeleteModalOpen(false);
            deleteHandler();
          }}
        />
      )}
    </>
  );
};
